import ErrorDialog from '@/components/common/dialogs/ErrorDialog';
import ProgressDialog from '@/components/common/dialogs/ProgressDialog';
import TableDialog from '@/components/common/dialogs/TableDialog';
import { CommonDialogContext, ErrorObject } from '@/context/CommonDialogContext';
import { TableSection } from '@/types/table';
import { ConfirmProvider, useConfirm } from 'material-ui-confirm';
import { useState } from 'react';

/**
 * Offers different kinds of generic dialogs that can be used independently
 * and only needing a function call to reach.
 */
export const DialogsProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <ConfirmProvider>
      <InnerDialogsProvider>{children}</InnerDialogsProvider>
    </ConfirmProvider>
  );
};

const InnerDialogsProvider = ({ children }: { children: React.ReactNode }) => {
  const [openError, setOpenError] = useState(false);
  const [errorObject, setErrorObject] = useState<ErrorObject>();
  const [openProgress, setOpenProgress] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [openTable, setOpenTable] = useState(false);
  const [tableTitle, setTableTitle] = useState('');
  const [tableSections, setTableSections] = useState<TableSection[]>([]);
  const confirm = useConfirm();

  const showError = (errorObject: ErrorObject) => {
    setErrorObject(errorObject);
    setOpenError(true);
  };

  const showProgress = (message: string) => {
    setProgressMessage(message);
    setOpenProgress(true);
  };

  const closeProgress = () => {
    setOpenProgress(false);
    setProgressMessage('');
  };

  const showTable = (title: string, sections: TableSection[]) => {
    setTableTitle(title);
    setTableSections(sections);
    setOpenTable(true);
  };

  const closeTable = () => {
    setOpenProgress(false);
    setProgressMessage('');
  };

  return (
    <CommonDialogContext.Provider
      value={{
        showError: showError,
        showProgress: showProgress,
        closeProgress: closeProgress,
        showTable: showTable,
        closeTable: closeTable,
        confirm: confirm
      }}
    >
      {children}
      <ErrorDialog
        open={openError}
        onClose={() => setOpenError(false)}
        title={errorObject?.title}
        message={errorObject ? errorObject.message : ''}
        stacktrace={errorObject?.stacktrace}
      />
      <ProgressDialog open={openProgress} message={progressMessage} onClose={() => setOpenProgress(false)} />
      <TableDialog open={openTable} title={tableTitle} sections={tableSections} onClose={() => setOpenTable(false)} />
    </CommonDialogContext.Provider>
  );
};
